/**
 * HRJobPostings — live list of job postings from /v1/hr/job-postings.
 * Selecting a posting drills into its candidates (handled by Dashboard.tsx).
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical P1 design system).
 */
import { SectionTitle, LoadingState, ErrorState, EmptyState } from '../../DashboardShared'
import { useAsync, fetchJobPostings } from '../../api'
import type { ApiHRJobPosting } from '../../types'
import { ink, statusJob, surface } from '../../../../theme/tokens'

const STATUS_BADGE: Record<string, { bg: string; color: string }> = {
  draft:  { bg: statusJob.draft.bg,  color: statusJob.draft.text },
  active: { bg: statusJob.active.bg, color: statusJob.active.text },
  paused: { bg: statusJob.paused.bg, color: statusJob.paused.text },
  closed: { bg: statusJob.closed.bg, color: statusJob.closed.text },
}

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_BADGE[status] ?? { bg: surface.panel, color: ink.secondary }
  return (
    <span className="k-badge" style={{ background: cfg.bg, color: cfg.color }}>
      <span className="k-badge-dot" />{status.toUpperCase()}
    </span>
  )
}

interface HRJobPostingsProps {
  onSelectJob: (job: ApiHRJobPosting) => void
}

export default function HRJobPostings({ onSelectJob }: HRJobPostingsProps) {
  const { data, loading, error } = useAsync(fetchJobPostings, [])

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const jobs = data ?? []

  return (
    <div className="page-enter flex flex-col gap-4">
      <SectionTitle title="Job Postings" subtitle={`${jobs.filter(j => j.status === 'active').length} active · ${jobs.length} total`} />
      <div className="k-card">
        {jobs.length === 0 ? (
          <EmptyState message="No job postings yet" />
        ) : (
          <table className="closr-table hr-table w-full">
            <thead>
              <tr>
                <th className="text-left">Title</th>
                <th className="text-left">Department</th>
                <th className="text-left">Location</th>
                <th className="text-left">Status</th>
                <th className="text-left">Applicants</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {jobs.map(j => (
                <tr key={j.id} className="cursor-pointer" onClick={() => onSelectJob(j)}>
                  <td className="font-medium text-ink-primary">{j.title}</td>
                  <td className="text-12 text-ink-secondary">{j.department || '—'}</td>
                  <td className="text-12 text-ink-secondary">{j.location || '—'}</td>
                  <td><StatusBadge status={j.status} /></td>
                  <td className="text-12 text-ink-body font-mono">{j.applicationsCount}</td>
                  <td>
                    <button
                      onClick={(e) => { e.stopPropagation(); onSelectJob(j) }}
                      className="text-11 text-accent font-medium hover:underline whitespace-nowrap"
                    >
                      View candidates →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
